import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrowUp, Loader2 } from 'lucide-react';
import {
  AttachButton, AttachmentThumbs, attachmentsNote, useImageAttachments,
} from '../Offers/proposal/attachments';

const ChatInput = ({
  onSendMessage,
  isLoading = false,
  placeholder = "Écris ta question ici…",
  draft = null,
  allowAttachments = true,
}) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  // Images jointes (capture, photo de référence) : Nexus les ouvre avec son outil de vision.
  const attachments = useImageAttachments();

  // Message prérempli par une page (ex. « applique les commentaires ») : l'utilisateur le relit puis l'envoie.
  useEffect(() => {
    if (!draft) return;
    setMessage(draft.text || '');
    if (inputRef.current) inputRef.current.focus();
  }, [draft]);

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Le champ grandit avec le texte (jusqu'à ~6 lignes), puis défile.
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 160)}px`;
  }, [message]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if ((!trimmed && !attachments.items.length) || isLoading || attachments.busy) return;
    onSendMessage((trimmed || 'Regarde les images jointes.') + attachmentsNote(attachments.items));
    setMessage('');
    attachments.clear();
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSubmit(event);
    }
  };

  const canSend = !isLoading && !attachments.busy && Boolean(message.trim() || attachments.items.length);
  return (
    <form onSubmit={handleSubmit} className="shrink-0 border-t border-slate-100 bg-white px-3 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 sm:px-4 sm:pt-3">
      {(attachments.items.length > 0 || attachments.error) && (
        <div className="mb-2 space-y-1">
          <AttachmentThumbs items={attachments.items} onRemove={attachments.remove} />
          {attachments.error && <p className="text-xs text-red-600">{attachments.error}</p>}
        </div>
      )}
      <div className="flex items-end gap-1.5 rounded-2xl border border-slate-200 bg-white px-2 py-1.5 shadow-sm transition focus-within:border-indigo-300 focus-within:ring-4 focus-within:ring-indigo-100">
        {allowAttachments && <AttachButton attachments={attachments} className="mb-0.5" />}
        <textarea
          ref={inputRef}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          {...(allowAttachments ? attachments.inputHandlers : {})}
          rows={1}
          placeholder={placeholder}
          disabled={isLoading}
          className="max-h-40 min-h-[2.25rem] flex-1 resize-none bg-transparent px-1 py-2 text-base leading-5 text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:text-slate-400 sm:text-sm"
        />
        <button
          type="submit"
          disabled={!canSend}
          aria-label="Envoyer"
          title="Envoyer"
          className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </div>
      <p className="mt-1.5 hidden px-1 text-[11px] text-slate-400 sm:block">Entrée pour envoyer · Maj+Entrée pour aller à la ligne</p>
    </form>
  );
};

ChatInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  draft: PropTypes.shape({ text: PropTypes.string }),
  allowAttachments: PropTypes.bool,
};

export default ChatInput;
